import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import BrandMark from "./BrandMark";
import { CONTACT } from "../lib/content";

const construction = [
  { to: "/construction", label: "Overview" },
  { to: "/custom-homes", label: "Custom Homes" },
  { to: "/renovations", label: "Renovations & Additions" },
  { to: "/kitchen-and-bath", label: "Kitchen & Bath" },
  { to: "/commercial-construction", label: "Commercial" },
];

const recovery = [
  { to: "/federal-services", label: "Overview" },
  { to: "/disaster-recovery", label: "Disaster Recovery & FEMA PA" },
  { to: "/federal", label: "Federal Construction & Teaming" },
];

const about = [
  { to: "/about", label: "Our Story" },
  { to: "/team", label: "Leadership & Team" },
  { to: "/work-with-us", label: "Work With Us" },
  { to: "/faq", label: "FAQ" },
];

function Dropdown({ label, href, items, isDark, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  // Mouse: hover opens. Touch/pen: first tap opens, second tap follows the link.
  const onEnter = (e) => { if (e.pointerType === "mouse") setOpen(true); };
  const onLeave = (e) => { if (e.pointerType === "mouse") setOpen(false); };
  const onClick = (e) => { if (!open) { e.preventDefault(); setOpen(true); } };

  return (
    <div ref={ref} className="relative" onPointerEnter={onEnter} onPointerLeave={onLeave}>
      <NavLink
        to={href}
        data-testid={testid}
        onClick={onClick}
        aria-expanded={open}
        className={`!inline-flex items-center gap-1 whitespace-nowrap text-[12px] tracking-[0.12em] uppercase font-medium link-underline ${
          isDark ? "text-cream-light/90 hover:text-cream-light" : "text-ink/80 hover:text-ink"
        }`}
      >
        {label} <ChevronDown size={12} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </NavLink>
      <div data-testid={`${testid}-menu`} className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all ${open ? "opacity-100 visible" : "opacity-0 invisible"}`}>
        <div className="bg-cream-light border border-ink/15 shadow-lg min-w-[260px]">
          {items.map((s) => (
            <NavLink
              key={s.to}
              to={s.to}
              className={({ isActive }) => `block px-5 py-3 text-[12px] tracking-[0.12em] uppercase font-medium border-b border-ink/10 last:border-b-0 transition-colors ${isActive ? "bg-ink text-cream-light" : "text-ink hover:bg-cream"}`}
            >
              {s.label}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Nav({ transparentOnTop = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mSec, setMSec] = useState(null); // which mobile section is expanded
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setMSec(null); }, [loc.pathname]);

  const isDark = transparentOnTop && !scrolled;

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || !transparentOnTop
          ? "bg-cream-light/95 backdrop-blur-md border-b border-ink/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 flex items-center justify-between h-20 gap-4">
        <div className={isDark ? "text-cream-light" : "text-ink"}>
          <div className="sm:hidden">
            <BrandMark variant="icon" tone={isDark ? "light" : "dark"} size="sm" />
          </div>
          <div className="hidden sm:block xl:hidden">
            <BrandMark variant="compact" tone={isDark ? "light" : "dark"} size="sm" />
          </div>
          <div className="hidden xl:block">
            <BrandMark variant="compact" tone={isDark ? "light" : "dark"} size="md" />
          </div>
        </div>

        <nav className="hidden lg:flex items-center gap-3 xl:gap-6 2xl:gap-8">
          <Dropdown label="Construction" href="/construction" items={construction} isDark={isDark} testid="nav-construction-toggle" />
          <Dropdown label="Disaster Recovery" href="/federal-services" items={recovery} isDark={isDark} testid="nav-recovery-toggle" />
          <NavLink
            to="/expert-witness"
            data-testid="nav-link-expert-witness"
            className={({ isActive }) =>
              `whitespace-nowrap text-[12px] tracking-[0.12em] uppercase font-medium link-underline ${
                isDark ? "text-cream-light/90 hover:text-cream-light" : "text-ink/80 hover:text-ink"
              } ${isActive ? (isDark ? "text-cream-light" : "text-ink") : ""}`
            }
          >
            Expert Witness
          </NavLink>
          <NavLink
            to="/projects"
            data-testid="nav-link-projects"
            className={({ isActive }) =>
              `whitespace-nowrap text-[12px] tracking-[0.12em] uppercase font-medium link-underline ${
                isDark ? "text-cream-light/90 hover:text-cream-light" : "text-ink/80 hover:text-ink"
              } ${isActive ? (isDark ? "text-cream-light" : "text-ink") : ""}`
            }
          >
            Our Work
          </NavLink>
          <Dropdown label="About" href="/about" items={about} isDark={isDark} testid="nav-about-toggle" />
          <a
            href={CONTACT.phoneHref}
            data-testid="nav-tap-to-call"
            className={`!hidden xl:!inline-block text-[12px] tracking-[0.1em] font-medium link-underline whitespace-nowrap ${
              isDark ? "text-cream-light/90 hover:text-cream-light" : "text-ink/80 hover:text-ink"
            }`}
          >
            {CONTACT.phone}
          </a>
          <Link to="/contact" data-testid="nav-cta-start-project" className="btn-copper !py-3 !px-5 !text-[11px] whitespace-nowrap">
            Start a Project
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen(!open)}
          className={`lg:hidden shrink-0 ${isDark ? "text-cream-light" : "text-ink"}`}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-cream-light border-t border-ink/10 max-h-[85vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-2">
            {/* Construction accordion */}
            <button className="flex items-center justify-between py-3 text-ink text-[13px] uppercase tracking-[0.14em] font-medium border-b border-ink/10" onClick={() => setMSec(mSec === "construction" ? null : "construction")}>
              Construction <ChevronDown size={16} className={`transition-transform ${mSec === "construction" ? "rotate-180" : ""}`} />
            </button>
            {mSec === "construction" && (
              <div className="flex flex-col pl-4 pb-2">
                {construction.map((s) => (
                  <NavLink key={s.to} to={s.to} className="text-ink/80 text-sm py-2 border-b border-ink/5 last:border-0">{s.label}</NavLink>
                ))}
              </div>
            )}

            {/* Recovery accordion */}
            <button className="flex items-center justify-between py-3 text-ink text-[13px] uppercase tracking-[0.14em] font-medium border-b border-ink/10" onClick={() => setMSec(mSec === "recovery" ? null : "recovery")}>
              Disaster Recovery <ChevronDown size={16} className={`transition-transform ${mSec === "recovery" ? "rotate-180" : ""}`} />
            </button>
            {mSec === "recovery" && (
              <div className="flex flex-col pl-4 pb-2">
                {recovery.map((s) => (
                  <NavLink key={s.to} to={s.to} className="text-ink/80 text-sm py-2 border-b border-ink/5 last:border-0">{s.label}</NavLink>
                ))}
              </div>
            )}

            <NavLink to="/expert-witness" className="py-3 text-ink text-[13px] uppercase tracking-[0.14em] font-medium border-b border-ink/10">Expert Witness</NavLink>
            <NavLink to="/projects" className="py-3 text-ink text-[13px] uppercase tracking-[0.14em] font-medium border-b border-ink/10">Our Work</NavLink>

            {/* About accordion */}
            <button className="flex items-center justify-between py-3 text-ink text-[13px] uppercase tracking-[0.14em] font-medium border-b border-ink/10" onClick={() => setMSec(mSec === "about" ? null : "about")}>
              About <ChevronDown size={16} className={`transition-transform ${mSec === "about" ? "rotate-180" : ""}`} />
            </button>
            {mSec === "about" && (
              <div className="flex flex-col pl-4 pb-2">
                {about.map((s) => (
                  <NavLink key={s.to} to={s.to} className="text-ink/80 text-sm py-2 border-b border-ink/5 last:border-0">{s.label}</NavLink>
                ))}
              </div>
            )}

            <NavLink to="/contact" className="py-3 text-ink text-[13px] uppercase tracking-[0.14em] font-medium border-b border-ink/10">Contact</NavLink>

            <a href={CONTACT.phoneHref} data-testid="mobile-tap-to-call" className="btn-copper mt-4 justify-center">
              Call {CONTACT.phone}
            </a>
            <Link to="/contact" className="btn-dark mt-2 justify-center">Start a Project</Link>
          </div>
        </div>
      )}
    </header>
  );
}
